/**
 * @fileoverview Okta Authentication Store
 *
 * Integrates with @okta/okta-auth-js for reactive authentication state.
 * Uses direct oktaAuth instance for all operations (no composable).
 * Syncs user profile with backend after Okta authentication.
 * Uses Okta SDK for all token management - NO localStorage token operations.
 *
 * Architecture:
 * - Reactive state: Direct subscription to oktaAuth.authStateManager
 * - Method calls: Uses imported oktaAuth instance from @/config/okta
 * - Token flow: Okta token → /auth/sync → Internal session token → API calls
 *
 * IMPORTANT: Cannot use useOktaAuth() composable in Pinia stores (returns undefined).
 * Must use direct oktaAuth instance and subscribe to authStateManager.
 */

import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import oktaAuth from '@/config/okta'
import * as authApi from '@/api/auth'
import { setTokenGetter, setSessionExpiredHandler } from '@/api/client'

// Token refresh configuration constants
const REFRESH_BUFFER_MS = 2 * 60 * 1000 // Refresh 2 minutes before expiration
const MIN_REFRESH_INTERVAL_MS = 60 * 1000 // Don't refresh more often than every 1 minute
const MAX_REFRESH_RETRIES = 3 // Maximum number of retry attempts for token refresh

/**
 * @typedef {Object} User
 * @property {string} id - User ID
 * @property {string} email - User email
 * @property {string} display_name - User display name
 * @property {string} role - User role (admin, instructor, student)
 * @property {boolean} is_active - Whether user is active
 * @property {string} created_at - ISO timestamp of creation
 * @property {string} [updated_at] - ISO timestamp of last update
 */

/**
 * Pinia store for Okta authentication and user management
 *
 * @example
 * import { useAuthStore } from '@/stores/auth'
 *
 * const authStore = useAuthStore()
 *
 * if (authStore.isAuthenticated) {
 *   console.debug('User:', authStore.user.email)
 * }
 */
export const useAuthStore = defineStore('auth', () => {
  console.debug('[Auth Store] ========== STORE INITIALIZING ==========')

  // Get initial auth state from Okta SDK directly
  // IMPORTANT: Cannot use useOktaAuth() composable - it only works in Vue components
  const initialAuthState = oktaAuth.authStateManager.getAuthState()
  console.debug('[Auth Store] Initial auth state from oktaAuth.authStateManager:', initialAuthState)

  // Create reactive ref for auth state
  /** @type {import('vue').Ref<any>} */
  const authState = ref(initialAuthState)

  console.debug('[Auth Store] authState.value (initial):', authState.value)
  console.debug(
    '[Auth Store] authState.value.isAuthenticated (initial):',
    authState.value?.isAuthenticated
  )

  // Subscribe to auth state changes from Okta SDK
  // This makes authState reactive to Okta authentication events
  oktaAuth.authStateManager.subscribe(newState => {
    console.debug('[Auth Store] authStateManager.subscribe: Auth state updated:', newState)
    authState.value = newState
  })

  console.debug('[Auth Store] Subscribed to oktaAuth.authStateManager')

  // Local state (synced with backend)
  /** @type {import('vue').Ref<User|null>} */
  const user = ref(null)

  /** @type {import('vue').Ref<string|null>} */
  const sessionToken = ref(null)

  /** @type {import('vue').Ref<boolean>} */
  const isLoading = ref(false)

  /** @type {import('vue').Ref<boolean>} */
  const profileSynced = ref(false)

  /** @type {import('vue').Ref<Promise<void>|null>} Promise that resolves when profile sync completes */
  const syncPromise = ref(null)

  // Token refresh state
  /** @type {import('vue').Ref<number|null>} Timestamp when the session token expires (Date.now() format) */
  const tokenExpiresAt = ref(null)

  /** @type {import('vue').Ref<number|null>} Timer ID for scheduled token refresh */
  let refreshTimerId = null

  /** @type {import('vue').Ref<boolean>} Flag to prevent concurrent refresh attempts */
  const isRefreshing = ref(false)
  /** @type {Promise<boolean>|null} */
  let refreshPromise = null
  /** @type {boolean} */
  let isRedirectingToLogin = false

  // Getters
  /**
   * Is user authenticated with Okta?
   * Derived from Okta SDK state via reactive authState ref.
   * @returns {boolean}
   */
  const isAuthenticated = computed(() => {
    const result = authState.value?.isAuthenticated ?? false
    console.debug('[Auth Store] isAuthenticated computed:', {
      authState: authState.value,
      isAuthenticated: authState.value?.isAuthenticated,
      result
    })
    return result
  })

  /**
   * Get access token for API calls
   * Returns internal session token if available, otherwise Okta token
   * @returns {string|null}
   */
  const accessToken = computed(() => {
    // Prefer internal session token for API calls
    if (sessionToken.value) {
      return sessionToken.value
    }
    // Fall back to Okta token (used for initial /auth/sync call)
    return authState.value?.accessToken?.accessToken ?? null
  })

  /**
   * Check if auth is ready for API calls
   * True when authenticated with Okta AND profile has been synced (session token available)
   * @returns {boolean}
   */
  const isReady = computed(() => {
    return isAuthenticated.value && profileSynced.value && !!sessionToken.value
  })

  /**
   * Check if current user has admin role
   * @returns {boolean}
   */
  const isAdmin = computed(() => {
    return user.value?.role === 'admin'
  })

  /**
   * Check if current user has instructor role
   * @returns {boolean}
   */
  const isInstructor = computed(() => {
    return user.value?.role === 'instructor'
  })

  /**
   * Get current user's role
   * @returns {string|null}
   */
  const userRole = computed(() => {
    return user.value?.role || null
  })

  // Actions

  /**
   * Schedule token refresh before expiration
   * Calculates when to refresh based on expires_in and schedules a timer.
   *
   * @param {number} expiresIn - Token lifetime in seconds
   * @private
   */
  function scheduleTokenRefresh(expiresIn) {
    // Clear any existing timer
    if (refreshTimerId) {
      clearTimeout(refreshTimerId)
      refreshTimerId = null
    }

    // Calculate expiration timestamp
    const expiresAt = Date.now() + expiresIn * 1000
    tokenExpiresAt.value = expiresAt

    // Calculate when to refresh (2 minutes before expiration, but at least 1 minute from now)
    const refreshAt = expiresAt - REFRESH_BUFFER_MS
    const timeUntilRefresh = Math.max(refreshAt - Date.now(), MIN_REFRESH_INTERVAL_MS)

    console.debug('[Auth Store] scheduleTokenRefresh:', {
      expiresIn,
      expiresAt: new Date(expiresAt).toISOString(),
      refreshAt: new Date(refreshAt).toISOString(),
      timeUntilRefreshMs: timeUntilRefresh,
      timeUntilRefreshMin: Math.round(timeUntilRefresh / 60000)
    })

    // Schedule the refresh
    refreshTimerId = setTimeout(async () => {
      console.debug('[Auth Store] Scheduled token refresh triggered')
      await refreshSessionToken()
    }, timeUntilRefresh)
  }

  /**
   * Check if token needs refresh
   * Returns true if token is close to expiration or already expired.
   *
   * @returns {boolean}
   * @private
   */
  function shouldRefreshToken() {
    if (!tokenExpiresAt.value) {
      return false
    }

    const now = Date.now()
    const timeUntilExpiration = tokenExpiresAt.value - now

    // Refresh if less than 2 minutes until expiration
    return timeUntilExpiration < REFRESH_BUFFER_MS
  }

  /**
   * Refresh session token
   * Gets a fresh Okta token and calls /auth/sync to get a new session token.
   * Includes retry logic with exponential backoff.
   *
   * @param {number} [retryAttempt=0] - Current retry attempt number
   * @returns {Promise<boolean>} True if refresh succeeded, false otherwise
   * @private
   */
  async function refreshSessionToken(retryAttempt = 0) {
    // Reuse in-flight refresh to avoid concurrent refresh races.
    if (refreshPromise) {
      console.debug('[Auth Store] refreshSessionToken: Waiting for in-flight refresh')
      return refreshPromise
    }

    const runRefresh = async attempt => {
      try {
        console.debug('[Auth Store] refreshSessionToken: Starting token refresh...', {
          retryAttempt: attempt,
          maxRetries: MAX_REFRESH_RETRIES
        })

        // Get fresh Okta access token
        const oktaToken = await oktaAuth.getAccessToken()
        if (!oktaToken) {
          throw new Error('No Okta access token available for refresh')
        }

        // Call /auth/sync to get new session token
        const authResponse = await authApi.syncProfile(oktaToken)

        // Update session token and schedule next refresh
        sessionToken.value = authResponse.access_token
        user.value = authResponse.user
        profileSynced.value = true
        scheduleTokenRefresh(authResponse.expires_in)

        console.debug('[Auth Store] refreshSessionToken: Token refresh successful')
        return true
      } catch (error) {
        console.error('[Auth Store] refreshSessionToken: Failed to refresh token:', error)

        if (attempt < MAX_REFRESH_RETRIES) {
          const delayMs = Math.pow(2, attempt) * 1000 // 1s, 2s, 4s
          console.debug(
            `[Auth Store] refreshSessionToken: Retrying in ${delayMs}ms (attempt ${attempt + 1}/${MAX_REFRESH_RETRIES})`
          )
          await new Promise(resolve => setTimeout(resolve, delayMs))
          return runRefresh(attempt + 1)
        }

        console.error('[Auth Store] refreshSessionToken: Max retries exceeded, token refresh failed')
        return false
      }
    }

    isRefreshing.value = true
    refreshPromise = runRefresh(retryAttempt).finally(() => {
      isRefreshing.value = false
      refreshPromise = null
    })
    return refreshPromise
  }

  /**
   * Handle unrecoverable session expiration by clearing local state and forcing re-authentication.
   *
   * @param {{ reason?: string, status?: number, url?: string }} [context]
   * @returns {Promise<void>}
   */
  async function handleSessionExpired(context = {}) {
    if (isRedirectingToLogin) {
      return
    }

    isRedirectingToLogin = true
    clearLocalState()

    const originalUri =
      typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}${window.location.hash}`
        : '/dashboard'

    try {
      console.warn('[Auth Store] Session expired, redirecting to login', context)
      await oktaAuth.signInWithRedirect({ originalUri })
    } finally {
      // Reset after a delay in case redirect is blocked or canceled.
      setTimeout(() => {
        isRedirectingToLogin = false
      }, 1000)
    }
  }

  /**
   * Handle tab visibility change
   * Refreshes token if tab becomes visible and token is close to expiration.
   *
   * @private
   */
  function handleVisibilityChange() {
    if (document.visibilityState === 'visible' && isAuthenticated.value) {
      console.debug('[Auth Store] Tab became visible, checking token expiration')

      if (shouldRefreshToken()) {
        console.debug('[Auth Store] Token needs refresh, refreshing now')
        refreshSessionToken()
      }
    }
  }

  /**
   * Sync user profile with backend
   * Called after successful Okta authentication.
   * Exchanges Okta token for internal session token.
   *
   * @returns {Promise<void>}
   * @example
   * await authStore.syncUserProfile()
   */
  async function syncUserProfile() {
    if (profileSynced.value) {
      console.debug('[Auth Store] syncUserProfile: Already synced, skipping')
      return
    }

    // If sync is already in progress, wait for it
    if (syncPromise.value) {
      console.debug('[Auth Store] syncUserProfile: Sync in progress, waiting...')
      return syncPromise.value
    }

    // Create and store the sync promise
    syncPromise.value = (async () => {
      try {
        isLoading.value = true

        console.debug('[Auth Store] syncUserProfile: Starting profile sync...')

        // Get Okta access token from SDK using the imported instance
        const oktaToken = await oktaAuth.getAccessToken()
        if (!oktaToken) {
          throw new Error('No Okta access token available')
        }

        console.debug(
          '[Auth Store] syncUserProfile: Got Okta token (first 30 chars):',
          oktaToken.substring(0, 30) + '...'
        )

        // Backend validates Okta token and returns internal session token + user
        console.debug('[Auth Store] syncUserProfile: Calling /auth/sync...')
        const authResponse = await authApi.syncProfile(oktaToken)

        console.debug('[Auth Store] syncUserProfile: Response received:', {
          hasAccessToken: !!authResponse.access_token,
          accessTokenPrefix: authResponse.access_token
            ? authResponse.access_token.substring(0, 30) + '...'
            : null,
          tokenType: authResponse.token_type,
          expiresIn: authResponse.expires_in,
          user: authResponse.user
        })

        // Store internal session token for subsequent API calls
        sessionToken.value = authResponse.access_token
        user.value = authResponse.user
        profileSynced.value = true

        // Schedule proactive token refresh
        if (authResponse.expires_in) {
          scheduleTokenRefresh(authResponse.expires_in)
        }

        console.debug('[Auth Store] syncUserProfile: Session token stored successfully')
      } catch (error) {
        console.error('[Auth Store] syncUserProfile: Failed to sync user profile:', error)
        // Don't clear auth - Okta session is still valid
        throw error
      } finally {
        isLoading.value = false
        syncPromise.value = null
      }
    })()

    return syncPromise.value
  }

  /**
   * Wait for profile sync to complete
   * Use this before making API calls to ensure the session token is available.
   *
   * @returns {Promise<void>}
   * @example
   * await authStore.waitForSync()
   * // Now safe to make API calls
   */
  async function waitForSync() {
    // If already synced, return immediately
    if (profileSynced.value) {
      return
    }

    // If sync is in progress, wait for it
    if (syncPromise.value) {
      await syncPromise.value
      return
    }

    // If authenticated but not synced, trigger sync
    if (isAuthenticated.value && !profileSynced.value) {
      await syncUserProfile()
    }
  }

  /**
   * Get fresh access token for API calls
   * Returns internal session token if available.
   * Waits for profile sync to complete if in progress, or triggers sync if needed.
   *
   * @returns {Promise<string|null>} Access token
   * @example
   * const token = await authStore.getAccessToken()
   */
  async function getAccessToken() {
    // Return internal session token if already available
    if (sessionToken.value) {
      console.debug(
        '[Auth Store] getAccessToken: Returning session token (first 30 chars):',
        sessionToken.value.substring(0, 30) + '...'
      )
      return sessionToken.value
    }

    // If sync is in progress, wait for it to complete
    if (syncPromise.value) {
      console.debug('[Auth Store] getAccessToken: Waiting for sync to complete...')
      try {
        await syncPromise.value
        // After sync, return the session token
        if (sessionToken.value) {
          console.debug(
            '[Auth Store] getAccessToken: Sync complete, returning session token (first 30 chars):',
            sessionToken.value.substring(0, 30) + '...'
          )
          return sessionToken.value
        }
      } catch (error) {
        // Sync failed, but continue to try getting token
        console.error('[Auth Store] getAccessToken: Sync failed while getting access token:', error)
      }
    }

    // If authenticated with Okta but not synced yet, trigger sync
    if (isAuthenticated.value && !profileSynced.value) {
      console.debug('[Auth Store] getAccessToken: Authenticated but not synced, triggering sync...')
      try {
        await syncUserProfile()
        // After sync, return the session token
        if (sessionToken.value) {
          console.debug(
            '[Auth Store] getAccessToken: Sync complete, returning session token (first 30 chars):',
            sessionToken.value.substring(0, 30) + '...'
          )
          return sessionToken.value
        } else {
          // Sync completed but no session token - this shouldn't happen
          console.error(
            '[Auth Store] getAccessToken: Sync completed but no session token available'
          )
          return null
        }
      } catch (error) {
        // Sync failed - DO NOT fall back to Okta token as it's only valid for /auth/sync
        console.error(
          '[Auth Store] getAccessToken: Failed to sync profile, cannot make API calls:',
          error
        )
        return null
      }
    }

    // If we reach here, user is not authenticated or in an unexpected state
    console.warn('[Auth Store] getAccessToken: No valid token available', {
      isAuthenticated: isAuthenticated.value,
      profileSynced: profileSynced.value,
      hasSessionToken: !!sessionToken.value
    })
    return null
  }

  /**
   * Initiate login via Okta
   *
   * @param {string} originalUri - URI to redirect to after login
   * @returns {Promise<void>}
   * @example
   * await authStore.login('/dashboard')
   */
  async function login(originalUri = '/dashboard') {
    await oktaAuth.signInWithRedirect({ originalUri })
  }

  /**
   * Logout from Okta and backend
   *
   * Notifies backend for audit logging, clears local state,
   * and signs out from Okta (redirects to Okta, then back).
   *
   * @returns {Promise<void>}
   * @example
   * await authStore.logout()
   */
  async function logout() {
    try {
      // Notify backend of logout (for audit) using session token
      if (sessionToken.value) {
        await authApi.logout(sessionToken.value)
      }
    } catch (error) {
      console.error('Backend logout notification failed:', error)
    }

    // Clear local state
    clearLocalState()

    // Sign out from Okta (redirects to Okta, then back)
    await oktaAuth.signOut()
  }

  /**
   * Clear local auth state
   * Internal helper function.
   */
  function clearLocalState() {
    user.value = null
    sessionToken.value = null
    profileSynced.value = false
    tokenExpiresAt.value = null

    // Clear refresh timer
    if (refreshTimerId) {
      clearTimeout(refreshTimerId)
      refreshTimerId = null
    }
  }

  /**
   * Watch for auth state changes and sync profile
   *
   * IMPORTANT: This watcher monitors the authState ref (updated via authStateManager subscription).
   * It runs immediately with the initial state and on every subsequent change.
   */
  watch(
    authState,
    async newAuthState => {
      console.debug('[Auth Store] ========== WATCHER TRIGGERED ==========')
      console.debug('[Auth Store] Watch: newAuthState =', newAuthState)
      console.debug('[Auth Store] Watch: isAuthenticated =', newAuthState?.isAuthenticated)
      console.debug('[Auth Store] Watch: profileSynced =', profileSynced.value)
      console.debug(
        '[Auth Store] Watch: accessToken exists =',
        !!newAuthState?.accessToken?.accessToken
      )

      try {
        // Check if user is authenticated
        const isAuth = newAuthState?.isAuthenticated

        if (isAuth && !profileSynced.value) {
          console.debug(
            '[Auth Store] Watch: User authenticated but not synced, checking for access token...'
          )

          // Verify we actually have an access token before trying to sync
          const hasToken = !!newAuthState?.accessToken?.accessToken
          if (!hasToken) {
            console.debug(
              '[Auth Store] Watch: No access token yet, skipping sync (Okta still initializing)'
            )
            return
          }

          console.debug('[Auth Store] Watch: Conditions met, triggering syncUserProfile...')
          await syncUserProfile()
          console.debug('[Auth Store] Watch: syncUserProfile completed successfully')
        } else if (!isAuth) {
          console.debug('[Auth Store] Watch: Not authenticated, clearing local state')
          clearLocalState()
        } else if (isAuth && profileSynced.value) {
          console.debug('[Auth Store] Watch: Already synced, skipping')
        } else {
          console.debug('[Auth Store] Watch: Unexpected state:', {
            isAuth,
            profileSynced: profileSynced.value,
            hasAuthState: !!newAuthState
          })
        }
      } catch (error) {
        console.error('[Auth Store] Watch: Error in watcher:', error)
        console.error('[Auth Store] Watch: Error stack:', error.stack)
      }

      console.debug('[Auth Store] ========== WATCHER COMPLETE ==========')
    },
    { immediate: true, deep: true }
  )

  /**
   * Register token getter with API client
   * This allows the API client to access the current token without circular dependencies
   * IMPORTANT: Must be called after getAccessToken is defined
   */
  setTokenGetter(getAccessToken)
  setSessionExpiredHandler(handleSessionExpired)

  /**
   * Set up visibility change listener
   * Refreshes token when tab becomes visible if needed
   */
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handleVisibilityChange)
  }

  return {
    // State
    user,
    isLoading,
    authState,
    tokenExpiresAt,

    // Computed
    isAuthenticated,
    isReady,
    accessToken,
    isAdmin,
    isInstructor,
    userRole,

    // Actions
    login,
    logout,
    syncUserProfile,
    waitForSync,
    getAccessToken,
    refreshSessionToken,
    handleSessionExpired
  }
})
